import React, { useState, useEffect } from 'react';
import ContactCard from '../../../components/resellers/crm/ContactCard';
import styles from './CrmPage.module.css';

function generateUUID() {
  return crypto.randomUUID();
}

const API_BASE = 'https://kindred-cloudflare.shepherdn.workers.dev';
const DEFAULT_RESELLER_ID = 'reseller_001';
const DEFAULT_ADMIN_ID = 'admin_001';
const DEFAULT_MASTER_RESELLER_ID = 'master_001';

function CrmPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCRMData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE}/crm`, {
        method: 'GET',
        headers: {
          'x-role': 'reseller',
          'x-id': DEFAULT_RESELLER_ID,
        },
      });

      // If no index or contact data is found, seed a default contact
      if (res.status === 404) {
        console.warn('No CRM data found. Seeding default contact...');

        const defaultContact = {
          id: generateUUID(),
          name: 'Default Contact',
          status: 'Active',
          company: 'Kindred OS',
          resellerId: DEFAULT_RESELLER_ID,
          adminId: DEFAULT_ADMIN_ID,
          master_resellerId: DEFAULT_MASTER_RESELLER_ID,
        };

        // Contact files are the source of truth. Index.json is for fast lookup/cataloging only.
        const postRes = await fetch(`${API_BASE}/crm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-role': 'reseller',
            'x-id': DEFAULT_RESELLER_ID
          },
          body: JSON.stringify(defaultContact),
        });

        if (postRes.ok) {
          const result = await postRes.json();
          setContacts([result.contact]);
        } else {
          const errorData = await postRes.json();
          console.error('Seeding failed:', errorData.message);
          setError(`Failed to seed initial contact: ${errorData.message || 'Unknown error'}`);
          setContacts([]);
        }
      } else if (res.ok) {
        const data = await res.json();
        console.log('Fetched CRM data:', data);

        // The backend should be returning an array of complete contact objects
        if (Array.isArray(data)) {
          // Make sure all contacts have required IDs
          const validContacts = data.filter(contact => contact && contact.id);
          setContacts(validContacts);
          
          if (validContacts.length !== data.length) {
            console.warn(`Filtered out ${data.length - validContacts.length} invalid contacts`);
          }
        } else if (data?.contact) {
          // Single contact response
          setContacts([data.contact]);
        } else if (Array.isArray(data?.data)) {
          // Legacy wrapper format
          const validContacts = data.data.filter(contact => contact && contact.id);
          setContacts(validContacts);
        } else {
          console.warn('Unexpected CRM data structure:', data);
          setError('Received unexpected data format from server');
          setContacts([]);
        }
      } else {
        console.error('Unexpected response status:', res.status);
        setError(`Server returned status ${res.status}`);
      }
    } catch (err) {
      console.error('Failed to fetch CRM data:', err);
      setError(`Failed to fetch contacts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCRMData();
  }, []);

  // Adds a new contact and stores both the contact and updates the relevant index.json files
  const addContact = async () => {
    try {
      const newContact = {
        id: generateUUID(), // Generate a new UUID for the contact
        name: 'New Contact',
        status: 'New',
        company: 'New Company',
        resellerId: DEFAULT_RESELLER_ID,
        adminId: DEFAULT_ADMIN_ID,
        master_resellerId: DEFAULT_MASTER_RESELLER_ID,
        timestamp: new Date().toISOString(),
      };

      const res = await fetch(`${API_BASE}/crm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-role': 'reseller',
          'x-id': DEFAULT_RESELLER_ID
        },
        body: JSON.stringify(newContact),
      });

      const result = await res.json();
      if (res.ok) {
        // Use the contact from the result in case the server modified it
        setContacts(prev => [result.contact, ...prev]);
      } else {
        console.error('Add contact failed:', result.message);
        alert(`Failed to add contact: ${result.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
      alert(`Error adding contact: ${err.message}`);
    }
  };

  // Updates both the contact file and relevant entries in index.json
  const handleEdit = async (contactToEdit) => {
    try {
      // Ensure all required IDs are present
      const updated = {
        ...contactToEdit,
        resellerId: contactToEdit.resellerId || DEFAULT_RESELLER_ID,
        adminId: contactToEdit.adminId || DEFAULT_ADMIN_ID,
        master_resellerId: contactToEdit.master_resellerId || DEFAULT_MASTER_RESELLER_ID,
      };

      const res = await fetch(`${API_BASE}/crm`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-role': 'reseller',
          'x-id': DEFAULT_RESELLER_ID
        },
        body: JSON.stringify(updated),
      });

      if (res.ok) {
        const result = await res.json();
        // Update the contact in the state
        setContacts(prev => prev.map(c => (c.id === result.contact.id ? result.contact : c)));
      } else {
        const errorData = await res.json();
        console.error('Edit failed:', errorData.message);
        alert(`Failed to update contact: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating contact:', err);
      alert(`Error updating contact: ${err.message}`);
    }
  };

  // Removes contact from index.json and deletes the contact file itself
  const handleArchive = async (contactToArchive) => {
    try {
      if (!contactToArchive || !contactToArchive.id) {
        console.error('Invalid contact provided for archiving');
        return;
      }

      const res = await fetch(`${API_BASE}/crm`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-role': 'reseller',
          'x-id': DEFAULT_RESELLER_ID
        },
        body: JSON.stringify({ id: contactToArchive.id }),
      });

      if (res.ok) {
        // Remove the contact from state
        setContacts(prev => prev.filter(c => c.id !== contactToArchive.id));
      } else {
        const errorData = await res.json();
        console.error('Archive failed:', errorData.message);
        alert(`Failed to archive contact: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error archiving contact:', err);
      alert(`Error archiving contact: ${err.message}`);
    }
  };

  const refreshContacts = () => {
    fetchCRMData();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>CRM Management</h1>
        <div className={styles.headerButtons}>
          <button onClick={refreshContacts} className={styles.refreshButton}>↻ Refresh</button>
          <button onClick={addContact} className={styles.addButton}>+ Add Contact</button>
        </div>
      </div>
      <p>Here you can manage all client contacts, communication logs, and sales pipelines.</p>
      
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}
      
      {loading ? (
        <div className={styles.loading}>Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No contacts found. Click "Add Contact" to create your first contact.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {contacts.map(contact => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={handleEdit}
              onArchive={handleArchive}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CrmPage;